import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlatformRolePicker } from "@/components/pages/users/components/PlatformRolePicker";
import {
  useCustomRoles,
  usePlatformUserRoles,
  useReplacePlatformUserRole,
} from "@/components/pages/users/hooks/useCustomRoles";
import {
  guesteriaAdminKeys,
  useGuesteriaAdminById,
  useUpdateGuesteriaAdmin,
} from "@/components/pages/users/hooks/useGuesteriaAdmins";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useActiveDeletionJobForTarget } from "@/hooks/useDataDeletion";
import { useActiveExportJobForTarget } from "@/hooks/useDataExport";
import { DeletionDangerZone } from "@/components/common/data-deletion/DeletionDangerZone";
import { useDeletionFlow } from "@/components/common/data-deletion/useDeletionFlow";
import { ExportActionCard } from "@/components/common/data-export/ExportActionCard";
import { useExportFlow } from "@/components/common/data-export/useExportFlow";
import { isSuperAdminRole } from "@/utils/accessControl";
import { validateEmail } from "@/utils/validation";
import { DeletionTargetType } from "@/types/data-deletion";
import { ExportTargetType } from "@/types/data-export";
import type { UpdateGuesteriaAdminDto } from "@/types/guesteria-admin";

const NAME_MAX_LENGTH = 64;

export const SuperAdminUserEditPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();

  const { data: admin, isLoading } = useGuesteriaAdminById(id);
  const updateMutation = useUpdateGuesteriaAdmin(id);
  const replaceRoleMutation = useReplacePlatformUserRole(id);
  const {
    data: assignedPlatformRoles,
    isLoading: isAssignedPlatformRolesLoading,
  } = usePlatformUserRoles(id);
  const { data: platformRoles = [], isLoading: isPlatformRolesLoading } =
    useCustomRoles("platform");
  const { userProfile } = useAuthenticatedUser();
  const isSuperAdmin = isSuperAdminRole(userProfile);
  const queryClient = useQueryClient();
  const deletion = useDeletionFlow({
    onCompleted: () => {
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.all });
      navigate("/users");
    },
  });
  const { data: activeUserJob } = useActiveDeletionJobForTarget(
    DeletionTargetType.USER,
    id,
    isSuperAdmin,
  );
  const exportFlow = useExportFlow();
  const { data: activeUserExportJob } = useActiveExportJobForTarget(
    ExportTargetType.USER,
    id,
    isSuperAdmin,
  );

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedCustomRoleId, setSelectedCustomRoleId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (admin) {
      setEmail(admin.email);
      setFirstName(admin.firstName ?? "");
      setLastName(admin.lastName ?? "");
    }
  }, [admin]);

  useEffect(() => {
    if (assignedPlatformRoles) {
      setSelectedCustomRoleId(assignedPlatformRoles[0]?.customRoleId ?? null);
    }
  }, [assignedPlatformRoles]);

  const onBack = () => {
    navigate("/users");
  };

  const buildPayload = (): UpdateGuesteriaAdminDto => {
    const payload: UpdateGuesteriaAdminDto = {};
    const trimmedEmail = email.trim();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (trimmedEmail !== admin?.email) payload.email = trimmedEmail;
    if (trimmedFirst !== (admin?.firstName ?? ""))
      payload.firstName = trimmedFirst || undefined;
    if (trimmedLast !== (admin?.lastName ?? ""))
      payload.lastName = trimmedLast || undefined;

    return payload;
  };

  const onSave = async () => {
    const trimmedEmail = email.trim();

    if (!validateEmail(trimmedEmail).isValid) {
      toast.error(t("users.form.emailRequired", "Valid email is required"));
      return;
    }

    try {
      await updateMutation.mutateAsync(buildPayload());
      const assignedCustomRoleId =
        assignedPlatformRoles?.[0]?.customRoleId ?? null;
      if (selectedCustomRoleId !== assignedCustomRoleId) {
        await replaceRoleMutation.mutateAsync(selectedCustomRoleId);
      }
      toast.success(
        t(
          "users.form.guesteriaAdminUpdatedSuccessfully",
          "Guesteria admin updated successfully",
        ),
      );
      navigate("/users");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t(
              "users.form.failedToUpdateGuesteriaAdmin",
              "Failed to update Guesteria admin",
            );
      toast.error(message);
    }
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-foreground h-8 rounded-md bg-transparent px-3 py-1 text-base font-normal hover:bg-transparent"
        >
          <Undo2 className="h-6 w-6" />
          {t("users.page.back")}
        </Button>
      </div>

      <div className="bg-background flex flex-col gap-6 rounded-lg p-6">
        <h2 className="text-foreground text-lg font-semibold">
          {t("users.form.editGuesteriaAdmin", "Edit Guesteria Admin")}
        </h2>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">
            {t("common.loading", "Loading...")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              label={t("users.form.email")}
              placeholder={t("users.form.email", "Email")}
              type="email"
            />
            <Input
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              label={t("users.form.firstName")}
              placeholder={t("users.form.firstName", "First Name")}
              maxLength={NAME_MAX_LENGTH}
            />
            <Input
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              label={t("users.form.lastName")}
              placeholder={t("users.form.lastName", "Last Name")}
              maxLength={NAME_MAX_LENGTH}
            />
          </div>
        )}

        {!isLoading ? (
          <PlatformRolePicker
            roles={platformRoles}
            selectedRoleId={selectedCustomRoleId}
            isLoading={isPlatformRolesLoading || isAssignedPlatformRolesLoading}
            label={t("users.form.role")}
            description={t("users.form.platformRoleDescription")}
            onSelectedRoleIdChange={setSelectedCustomRoleId}
          />
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onBack} className="h-8 px-4">
            {t("users.form.cancel", "Cancel")}
          </Button>
          <Button
            variant="default"
            className="h-8 px-4"
            onClick={() => void onSave()}
            disabled={
              isLoading ||
              isPlatformRolesLoading ||
              isAssignedPlatformRolesLoading ||
              updateMutation.isPending ||
              replaceRoleMutation.isPending
            }
          >
            {updateMutation.isPending || replaceRoleMutation.isPending
              ? t("users.form.updating", "Updating...")
              : t("users.form.updateUser", "Update User")}
          </Button>
        </div>
      </div>

      {isSuperAdmin && admin ? (
        <ExportActionCard
          targetType={ExportTargetType.USER}
          description={t("dataExport.action.user.description")}
          buttonLabel={t("dataExport.action.user.button")}
          inFlight={Boolean(activeUserExportJob)}
          onViewInFlight={
            activeUserExportJob
              ? () => exportFlow.viewJob(activeUserExportJob.id)
              : undefined
          }
          onExport={() =>
            exportFlow.startExport({
              type: ExportTargetType.USER,
              id,
              label: admin.email,
            })
          }
        />
      ) : null}

      {isSuperAdmin && admin ? (
        <DeletionDangerZone
          targetType={DeletionTargetType.USER}
          description={t("dataDeletion.dangerZone.user.description")}
          buttonLabel={t("dataDeletion.dangerZone.user.button")}
          inFlight={Boolean(activeUserJob)}
          onViewInFlight={
            activeUserJob ? () => deletion.viewJob(activeUserJob.id) : undefined
          }
          onDelete={() =>
            deletion.startDeletion({
              type: DeletionTargetType.USER,
              id,
              label: admin.email,
              expectedConfirmation: admin.email,
            })
          }
        />
      ) : null}

      {exportFlow.dialogs}
      {deletion.dialogs}
    </div>
  );
};
